import {
  TECHNOLOGY_CONFIGURATIONS,
  TECHNOLOGY_IMPORTS,
  TECHNOLOGY_LIBRARY_IMPORTS,
  TECHNOLOGY_SETUP_MAPPING,
} from '@/core/config/technologies'
import { getPackageManager } from '@/core/hooks/useInstallationConfig/constants'
import {
  buildConfigurationMap,
  buildImportCode,
  buildRegistry,
  buildSetupCommandMap,
  computeGcgNames,
} from '@/core/hooks/useInstallationConfig/mappers'
import type { GcgNames } from '@/core/types/installation.type'
import type { LibraryData } from '@/core/types/library.type'

export const CONSUMER_TECHNOLOGIES = TECHNOLOGY_SETUP_MAPPING.map(({ id, label }) => ({ id, label }))

export interface BuildInstallationSnapshotParams {
  libraryData: LibraryData
  selectedTechnology: string
  technologySetupCommands: Record<string, Record<string, string>>
  gatewayPath: string
  gatewayWsPort?: string
  guid: string
  registryPath: string
  gsmuEndpoint: string
  isSecured: boolean
}

export type BuildInstallationGatewayParams = Omit<BuildInstallationSnapshotParams, 'libraryData' | 'selectedTechnology'>

export interface InstallationSnapshot {
  libraryId: string
  libraryName: string
  runtimeName: string
  consumerTechnology: string
  registryUrl: string
  graftNames: GcgNames
  setupCommandMap: Record<string, string>
  configurationMap: Record<string, string>
  importCode: string
}

export function buildInstallationSnapshot({
  libraryData,
  selectedTechnology,
  technologySetupCommands,
  gatewayPath,
  gatewayWsPort,
  guid,
  registryPath,
  gsmuEndpoint,
  isSecured,
}: BuildInstallationSnapshotParams): InstallationSnapshot {
  const packageManager = getPackageManager(libraryData.runtimeName)

  const registryUrl =
    selectedTechnology && gsmuEndpoint.trim() && registryPath.trim() && guid.trim()
      ? buildRegistry({
          selectedTechnology,
          gsmuEndpoint,
          registryPath,
          guid,
        })
      : ''

  const setupCommandMap = buildSetupCommandMap({
    selectedTechnology,
    technologySetupCommands,
    libraryData,
    registry: registryUrl,
    packageManager,
  })

  const configurationMap = buildConfigurationMap({
    selectedTechnology,
    technologyConfigurations: TECHNOLOGY_CONFIGURATIONS,
    isSecured,
    gatewayPath,
    gatewayWsPort,
    libraryData,
    packageManager,
  })

  const importCode = buildImportCode({
    selectedTechnology,
    technologyImports: TECHNOLOGY_IMPORTS,
    technologyLibraryImports: TECHNOLOGY_LIBRARY_IMPORTS,
    libraryData,
    classData: undefined,
    packageManager,
  })

  return {
    libraryId: libraryData.id,
    libraryName: libraryData.name,
    runtimeName: libraryData.runtimeName,
    consumerTechnology: selectedTechnology,
    registryUrl,
    graftNames: computeGcgNames({ selectedTechnology, libraryData }),
    setupCommandMap,
    configurationMap,
    importCode,
  }
}

export function buildInstallationSnapshotsForLibrary(
  libraryData: LibraryData,
  gatewayParams: BuildInstallationGatewayParams
): InstallationSnapshot[] {
  return CONSUMER_TECHNOLOGIES.map(({ id }) =>
    buildInstallationSnapshot({
      libraryData,
      selectedTechnology: id,
      ...gatewayParams,
    })
  )
}
